/**
 * How far the bed drops under a voice, and how it gets there.
 *
 * Background music is an ambient layer, never a participant. The moment anything
 * a creator recorded — a reading take, a narration, a video's own audio — is
 * playing, the music has to get out of its way, and the moment that ends it has
 * to come back. Two decisions live here rather than in the player, because both
 * are audible:
 *
 *  1. **How far.** Not "a bit quieter": a talker has to remain effortlessly
 *     intelligible over it, so the bed drops to roughly a tenth. `MUSIC_DUCK_LEVEL`
 *     is a *multiplier* on whatever the music's own level was, so the app's master
 *     volume and the track's own mix are both still respected.
 *  2. **How.** Abruptly changing a bed's gain under speech is a click, and a click
 *     is the one thing in a page that a learner will notice instead of the words.
 *     The change is therefore ramped over `MUSIC_FADE_MS` in small steps — and the
 *     ramp is a pure function of its endpoints, so the curve can be reasoned about
 *     (and tested) without an audio device.
 */

/** Level a bed drops to while a recording, narration or video is playing. */
export const MUSIC_DUCK_LEVEL = 0.12

/** How long a duck or an unduck takes. Long enough not to click, short enough
 * to feel like a response to the press that caused it. */
export const MUSIC_FADE_MS = 320

/** The interval the ramp is applied on. ~8 audible steps over a fade. */
export const MUSIC_FADE_STEP_MS = 40

/**
 * The levels a fade passes through, in order, ending exactly on `to`.
 *
 * The last step *is* the target, so a fade can never be left a fraction short of
 * where it was aiming — a bed that settles at 0.121 instead of 0.12 is a bed that
 * never actually stops ducking.
 */
export function fadeLevels(
  from: number,
  to: number,
  fadeMs: number = MUSIC_FADE_MS,
  stepMs: number = MUSIC_FADE_STEP_MS,
): number[] {
  const clamp = (v: number) => Math.min(1, Math.max(0, v))
  if (fadeMs <= 0 || stepMs <= 0) return [clamp(to)]
  const steps = Math.max(1, Math.ceil(fadeMs / stepMs))
  return Array.from({length: steps}, (_, i) =>
    clamp(from + (to - from) * ((i + 1) / steps)),
  )
}

/** Where the bed sits while a voice is over it. */
export function duckedLevel(level: number): number {
  return Math.min(1, Math.max(0, level * MUSIC_DUCK_LEVEL))
}
